import { useState } from 'react';
import { Link } from 'react-router-dom';

const faqs = [
  {
    q: 'Can you help me find and buy a specific car?',
    a: 'Absolutely! Tell us what you\'re looking for — make, model, year, budget — and our team will source it from our nationwide network, negotiate the best price, handle inspection, and deliver it to you. No dealership visits required.',
  },
  {
    q: 'Do I need to have my car paid off to sell it?',
    a: 'No. If you still owe money on your vehicle, we can work with your lender to pay off the loan directly. If your car is worth more than the payoff, you keep the difference. If it\'s worth less, we\'ll discuss your options.',
  },
  {
    q: 'What documents do I need to sell my car?',
    a: 'You\'ll need the vehicle title (or lien information if financed), a valid photo ID, and current registration. We handle all the DMV paperwork and title transfer for you.',
  },
  {
    q: 'How quickly will I get paid?',
    a: 'Most sellers receive payment the same day they bring in their vehicle. If we pick up your car, payment is typically processed within 24 hours via check or direct deposit.',
  },
  {
    q: 'Will you buy my car if it has mechanical issues or damage?',
    a: 'Yes! We buy vehicles in all conditions — running or not, cosmetic damage, mechanical issues, high mileage. Just be upfront about the condition so we can give you the most accurate offer.',
  },
  {
    q: 'Is there any cost or obligation?',
    a: 'Absolutely not. Getting a cash offer is 100% free with zero obligation. If you don\'t like our offer, no hard feelings — you\'re free to explore other options.',
  },
  {
    q: 'Do you serve Washington state residents?',
    a: 'Yes! We serve buyers and sellers throughout the Oregon Coast and Southwest Washington, including Longview, Kelso, Long Beach, and surrounding areas. Washington residents can buy from or sell their vehicles to us with no issues.',
  },
];

export default function SellCarFaq() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  return (
    <section className="py-20 bg-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="font-['Oswald'] text-4xl md:text-5xl font-bold text-[#1C2B1E] mb-4">
            Frequently Asked Questions
          </h2>
          <div className="w-24 h-1 bg-[#E8B84B] mx-auto mb-6"></div>
        </div>

        <div className="space-y-4">
          {faqs.map((faq, index) => {
            const btnId = `sell-faq-btn-${index}`;
            const panelId = `sell-faq-panel-${index}`;
            const isOpen = openIndex === index;
            return (
              <div
                key={index}
                className="bg-[#F7F5F0] rounded-xl border-2 border-[#E8E2D6] overflow-hidden hover:border-[#E8B84B] transition-all"
              >
                <button
                  id={btnId}
                  onClick={() => setOpenIndex(isOpen ? null : index)}
                  aria-expanded={isOpen}
                  aria-controls={panelId}
                  className="w-full px-6 py-5 flex items-center justify-between text-left hover:bg-white transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#1C2B1E]"
                >
                  <span className="font-semibold text-[#1C2B1E] pr-4">{faq.q}</span>
                  <div
                    aria-hidden="true"
                    className={`w-8 h-8 rounded-full bg-[#E8B84B] flex items-center justify-center flex-shrink-0 transition-transform ${
                      isOpen ? 'rotate-180' : ''
                    }`}
                  >
                    <i className="ri-arrow-down-s-line text-xl text-[#1C2B1E]"></i>
                  </div>
                </button>
                {isOpen && (
                  <div
                    id={panelId}
                    role="region"
                    aria-labelledby={btnId}
                    className="px-6 pb-5 pt-2 bg-white"
                  >
                    <p className="text-gray-700 leading-relaxed">{faq.a}</p>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="mt-12 bg-[#F7F5F0] rounded-2xl p-8 border-2 border-[#E8E2D6]">
          <h3 className="font-['Oswald'] text-xl font-bold text-[#1C2B1E] mb-4 text-center">
            Explore Related Services
          </h3>
          <div className="flex flex-wrap justify-center gap-3">
            <Link
              to="/trade-in"
              className="inline-flex items-center gap-2 px-5 py-3 bg-white border-2 border-[#E8E2D6] rounded-full font-medium text-[#1C2B1E] hover:border-[#E8B84B] transition-all whitespace-nowrap cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[#1C2B1E]"
            >
              <i className="ri-exchange-line text-[#7a5208]"></i>
              Trade-In Value
            </Link>
            <Link
              to="/inventory"
              className="inline-flex items-center gap-2 px-5 py-3 bg-white border-2 border-[#E8E2D6] rounded-full font-medium text-[#1C2B1E] hover:border-[#E8B84B] transition-all whitespace-nowrap cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[#1C2B1E]"
            >
              <i className="ri-car-line text-[#7a5208]"></i>
              Browse Inventory
            </Link>
            <Link
              to="/broker-services"
              className="inline-flex items-center gap-2 px-5 py-3 bg-white border-2 border-[#E8E2D6] rounded-full font-medium text-[#1C2B1E] hover:border-[#E8B84B] transition-all whitespace-nowrap cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[#1C2B1E]"
            >
              <i className="ri-service-line text-[#7a5208]"></i>
              Broker Services
            </Link>
            <Link
              to="/consignment"
              className="inline-flex items-center gap-2 px-5 py-3 bg-white border-2 border-[#E8E2D6] rounded-full font-medium text-[#1C2B1E] hover:border-[#E8B84B] transition-all whitespace-nowrap cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[#1C2B1E]"
            >
              <i className="ri-hand-coin-line text-[#7a5208]"></i>
              Consignment Option
            </Link>
            <Link
              to="/financing"
              className="inline-flex items-center gap-2 px-5 py-3 bg-white border-2 border-[#E8E2D6] rounded-full font-medium text-[#1C2B1E] hover:border-[#E8B84B] transition-all whitespace-nowrap cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[#1C2B1E]"
            >
              <i className="ri-bank-card-line text-[#7a5208]"></i>
              Financing Options
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
